import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { Dropdown } from 'react-bootstrap'
import Select from 'react-select'
import DatePicker from 'react-datepicker'
import XLSX from 'xlsx'
import { useReactToPrint } from 'react-to-print'

import FilterSearch from 'pages/parts/FilterSearch'
import PageTabs from 'pages/parts/PageTabs'
import HintIcon from 'pages/parts/HintIcon'
import { GET, POST, httpOk } from 'helpers/api'
import { formatDate, formatMoney, notify, returnSign } from 'helpers/helpers'

import AssetModal from './AssetModal'
import Groups from './Groups'
import Inventory from './Inventory'
import Forecast from './Forecast'
import Labels from './Labels'
import { formatPeriod, statusClass, statusLabel } from './utils'

// Основные средства: печь, холодильник, машина, здание — то, что дешевеет со временем.
//
// Покупка в прибыль не идёт целиком: каждый месяц 1-го числа сервер начисляет долю
// (амортизацию), и она ложится в P&L строкой «Оборудование и амортизация».
// Посуда сюда не заводится — это склад и акт списания боя.

function Index() {
	const { t } = useTranslation()
	const location = useLocation()

	const reduxAccount = useSelector(state => state.account)
	const query = new URLSearchParams(location.search)

	const [tab, setTab] = useState('list')
	const [data, setData] = useState([])
	const [counts, setCounts] = useState({})
	const [totals, setTotals] = useState({})
	const [poses, setPoses] = useState([])
	const [references, setReferences] = useState({ 'groups': [], 'terminals': [], 'employees': [] })
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [selected, setSelected] = useState({})
	const [modal, setModal] = useState({ 'show': false, 'id': null })
	const [labels, setLabels] = useState([])

	const [filterData, setFilterData] = useState({
		// По умолчанию — торговая точка, выбранная в шапке; «Все филиалы» — очисткой фильтра
		'pos_id': query.get('pos_id') ? Number(query.get('pos_id')) : (reduxAccount.pos_id || null),
		'group_id': null,
		'date': null,
		'search': '',
		'status': query.get('status') || 'in_use',
	})

	const printRef = useRef()
	const handlePrint = useReactToPrint({
		content: () => printRef.current,
	})

	const statusChips = [
		{ 'key': 'in_use', 'label': t('fa_in_use'), 'count': (counts.active ?? 0) + (counts.conserved ?? 0), 'className': 'btn-outline-primary' },
		{ 'key': 'ending', 'label': t('fa_ending'), 'count': counts.ending, 'className': 'btn-outline-warning', 'hint': t('fa_ending_hint') },
		{ 'key': 'maintenance', 'label': t('fa_maintenance_due'), 'count': counts.maintenance, 'className': 'btn-outline-danger' },
		{ 'key': 'conserved', 'label': t('fa_status_conserved'), 'count': counts.conserved, 'className': 'btn-outline-warning' },
		{ 'key': 'disposed', 'label': t('fa_status_disposed'), 'count': counts.disposed, 'className': 'btn-outline-secondary' },
		{ 'key': 'all', 'label': t('all'), 'count': counts.all, 'className': 'btn-outline-primary' },
	]

	function buildUrl(customFilterData = filterData) {
		var urlParams = ''

		if (customFilterData.pos_id)
			urlParams += returnSign(urlParams) + 'pos_id=' + customFilterData.pos_id
		if (customFilterData.group_id)
			urlParams += returnSign(urlParams) + 'group_id=' + customFilterData.group_id
		if (customFilterData.date)
			urlParams += returnSign(urlParams) + 'date=' + customFilterData.date
		if (customFilterData.search)
			urlParams += returnSign(urlParams) + 'search=' + encodeURIComponent(customFilterData.search)
		if (customFilterData.status)
			urlParams += returnSign(urlParams) + 'status=' + customFilterData.status

		return '/finance/fixed-assets' + urlParams
	}

	async function searchWithFilter(customFilterData = filterData) {
		const response = await GET(buildUrl(customFilterData), {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data.data)
			setCounts(response.data.counts)
			setTotals(response.data.totals)
			setSelected({})
		}

		setDropdownIsOpen(false)
	}

	function applyFilter(patch) {
		const nextFilterData = { ...filterData, ...patch }
		setFilterData(nextFilterData)
		searchWithFilter(nextFilterData)
	}

	async function getReferences() {
		const response = await GET('/finance/fixed-assets/references')
		if (httpOk(response)) setReferences(response.data)
	}

	async function getPoses() {
		const response = await GET('/helper/poses')
		if (httpOk(response)) setPoses(response.data)
	}

	// Начислить за прошлый месяц сейчас, не дожидаясь 1-го числа (догоняет и пропущенные)
	async function accrue() {
		const response = await POST('/finance/fixed-assets/accrue', {}, { loader: true })
		if (httpOk(response)) {
			notify(t('fa_accrued', { 'rows': response.data.rows, 'amount': formatMoney(response.data.amount) }), 'success')
			searchWithFilter()
		}
	}

	function openCard(id = null) {
		setModal({ 'show': true, 'id': id })
	}

	function closeCard(changed) {
		setModal({ 'show': false, 'id': null })
		if (changed) {
			searchWithFilter()
			getReferences()
		}
	}

	function toggleSelect(id) {
		const next = { ...selected }
		if (next[id]) delete next[id]
		else next[id] = true
		setSelected(next)
	}

	function toggleSelectAll() {
		if (Object.keys(selected).length === data.length) {
			setSelected({})
			return
		}
		const next = {}
		data.forEach(item => { next[item.id] = true })
		setSelected(next)
	}

	// Этикетки с QR: выбранные строки, а если ничего не выбрано — весь отфильтрованный список
	function printLabels(items = null) {
		const list = items || (Object.keys(selected).length > 0 ? data.filter(item => selected[item.id]) : data)
		if (list.length === 0) {
			notify(t('list_empty'))
			return
		}
		setLabels(list)
		setTimeout(() => handlePrint(), 100)
	}

	function exportToExcel() {
		const sheet = [[
			t('fa_inventory_number'), t('name2'), t('fa_serial_number'), t('fa_group'), t('pos'), t('quantity'),
			t('fa_commissioned_at'), t('fa_cost'), t('fa_accumulated'), t('fa_residual'), t('fa_monthly'),
			t('fa_months_left'), t('fa_end_period'), t('status'), t('fa_responsible'),
		]]
		data.forEach(item => {
			sheet.push([
				item.inventory_number, item.name, item.serial_number || '', item.group_name || '', item.pos_name, Number(item.quantity),
				formatDate(item.commissioned_at), Number(item.cost), Number(item.accumulated), Number(item.residual), Number(item.monthly),
				item.months_left ?? '', formatPeriod(item.end_period), statusLabel(t, item.status), item.responsible_name || '',
			])
		})
		sheet.push(['', t('total'), '', '', '', '', '', Number(totals.cost || 0), Number(totals.accumulated || 0), Number(totals.residual || 0), Number(totals.monthly || 0)])

		const ws = XLSX.utils.aoa_to_sheet(sheet)
		const wb = XLSX.utils.book_new()
		XLSX.utils.book_append_sheet(wb, ws, 'SheetJS')
		XLSX.writeFile(wb, t('fixed_assets') + (filterData.date ? ' ' + formatDate(filterData.date) : '') + '.xlsx')
	}

	useEffect(() => {
		searchWithFilter()
		getReferences()
		getPoses()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">
					{t('fixed_assets')}
				</h4>
				<div className="d-flex gap-2">
					<button className="btn-rounded btn btn-outline-primary" onClick={() => accrue()}
						title={t('fa_accrue_hint')}>
						{t('fa_accrue')}
					</button>
					<button className="btn-rounded btn btn-primary" onClick={() => openCard()}>
						{t('add')}
					</button>
				</div>
			</div>

			<PageTabs
				active={tab}
				onChange={setTab}
				tabs={[
					{ 'key': 'list', 'label': t('fa_tab_list') },
					{ 'key': 'groups', 'label': t('fa_tab_groups') },
					{ 'key': 'inventory', 'label': t('fa_tab_inventory') },
					{ 'key': 'forecast', 'label': t('fa_tab_forecast') },
				]} />

			{tab === 'groups' &&
				<Groups onChange={() => getReferences()} />
			}

			{tab === 'inventory' &&
				<Inventory posId={reduxAccount.pos_id} openCard={openCard} />
			}

			{tab === 'forecast' &&
				<Forecast posId={reduxAccount.pos_id} />
			}

			{tab === 'list' &&
				<div className="card">
					<div className="card-body">
						<div className="d-flex flex-wrap gap-2 mb-3">
							<div>
								<FilterSearch
									value={filterData.search}
									onSearchChange={(search) => applyFilter({ 'search': search })}>
								</FilterSearch>
							</div>
							<div>
								<Dropdown onToggle={() => setDropdownIsOpen(!dropdownIsOpen)} show={dropdownIsOpen}>
									<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
										{t('filter')}
									</Dropdown.Toggle>

									<Dropdown.Menu className="dropdown-filter">
										<div className="row">
											<div className="form-group col-md-6">
												<label>
													{t('pos')}
													<HintIcon text={t('fa_hint_filter_pos')} />
												</label>
												<Select
													isClearable
													options={poses}
													value={poses.find(option => option.id === filterData.pos_id) || ''}
													onChange={(option) => setFilterData({ ...filterData, 'pos_id': option ? option.id : null })}
													placeholder={t('fa_all_poses')}
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
											<div className="form-group col-md-6">
												<label>
													{t('fa_group')}
													<HintIcon text={t('fa_hint_filter_group')} />
												</label>
												<Select
													isClearable
													options={references.groups}
													value={references.groups.find(option => option.id === filterData.group_id) || ''}
													onChange={(option) => setFilterData({ ...filterData, 'group_id': option ? option.id : null })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
										</div>
										<div className="row">
											<div className="form-group col-md-6">
												<label>
													{t('fa_statement_date')}
													<HintIcon text={t('fa_statement_date_hint')} />
												</label>
												<div className="calendar-input">
													<DatePicker
														isClearable
														className="form-control"
														dateFormat="dd.MM.yyyy"
														selected={filterData.date ? new Date(filterData.date) : null}
														onChange={(date) => setFilterData({ ...filterData, 'date': date ? formatDate(date, 'yyyy-MM-dd') : null })}
														maxDate={new Date()}
														placeholderText={t('today')}
													/>
													<i className="uil uil-calendar-alt"></i>
												</div>
											</div>
										</div>
										<div className="row mt-3">
											<div className="col-md-12">
												<div className="d-flex justify-content-end gap-2">
													<button className="btn btn-secondary"
														onClick={() => setDropdownIsOpen(false)}>
														{t('cancel')}
													</button>
													<button className="btn btn-primary"
														onClick={() => searchWithFilter()}>
														{t('apply')}
													</button>
												</div>
											</div>
										</div>
									</Dropdown.Menu>
								</Dropdown>
							</div>
							<div>
								<button className="btn btn-outline-primary h-100" onClick={() => exportToExcel()}>
									EXCEL
								</button>
							</div>
							<div>
								<button className="btn btn-outline-primary h-100" onClick={() => printLabels()}
									title={t('fa_print_labels_hint')}>
									<i className="uil uil-qrcode-scan me-1"></i>
									{t('fa_print_labels')}
									{Object.keys(selected).length > 0 && ` [${Object.keys(selected).length}]`}
								</button>
							</div>

							<div className="d-flex flex-wrap gap-2 ms-auto">
								{statusChips.map(chip => (
									<button key={chip.key}
										title={chip.hint || ''}
										className={'btn ' + (filterData.status === chip.key ? chip.className.replace('outline-', '') : chip.className)}
										onClick={() => applyFilter({ 'status': chip.key })}>
										{chip.label} [{chip.count ?? 0}]
									</button>
								))}
							</div>
						</div>

						{filterData.date &&
							<div className="alert alert-info py-2">
								{t('fa_statement_on', { 'date': formatDate(filterData.date) })}
							</div>
						}

						<div className="table-responsive">
							<table className="table table-bordered table-hover mb-0">
								<thead>
									<tr>
										<th style={{ 'minWidth': '33px' }} className="text-center">
											<input type="checkbox" className="form-check-input"
												checked={data.length > 0 && Object.keys(selected).length === data.length}
												onChange={() => toggleSelectAll()} />
										</th>
										<th className="text-nowrap">{t('fa_inventory_number')}</th>
										<th style={{ 'width': '22%' }}>{t('name2')}</th>
										<th>{t('fa_group')}</th>
										<th>{t('pos')}</th>
										<th className="text-end">{t('quantity')}</th>
										<th className="text-end">{t('fa_cost')}</th>
										<th className="text-end">{t('fa_accumulated')}</th>
										<th className="text-end">{t('fa_residual')}</th>
										<th className="text-end text-nowrap">{t('fa_monthly')}</th>
										<th className="text-end text-nowrap">{t('fa_months_left')}</th>
										<th className="text-center">{t('status')}</th>
									</tr>
								</thead>
								<tbody>
									{data.map((item) => (
										<tr key={item.id} style={{ 'cursor': 'pointer' }} onClick={() => openCard(item.id)}>
											<td className="text-center" onClick={(e) => e.stopPropagation()}>
												<input type="checkbox" className="form-check-input"
													checked={!!selected[item.id]}
													onChange={() => toggleSelect(item.id)} />
											</td>
											<td className="text-nowrap">{item.inventory_number}</td>
											<td>
												{item.name}
												{item.serial_number &&
													<span className="text-muted"> · {item.serial_number}</span>
												}
												{item.is_maintenance_due &&
													<i className="uil uil-wrench text-danger ms-1" title={t('fa_maintenance_due_at', { 'date': formatDate(item.maintenance_due_at) })}></i>
												}
												{item.terminal_name &&
													<i className="uil uil-monitor text-muted ms-1" title={item.terminal_name}></i>
												}
											</td>
											<td>{item.group_name}</td>
											<td>{item.pos_name}</td>
											<td className="text-end">{formatMoney(item.quantity, 3)}</td>
											<td className="text-end text-nowrap">{formatMoney(item.cost)}</td>
											<td className="text-end text-nowrap">{formatMoney(item.accumulated)}</td>
											<td className="text-end text-nowrap fw-bold">{formatMoney(item.residual)}</td>
											<td className="text-end text-nowrap">{item.method === 'none' ? '—' : formatMoney(item.monthly)}</td>
											<td className={'text-end text-nowrap' + (item.is_ending ? ' text-warning fw-bold' : '')}
												title={item.end_period ? t('fa_end_period') + ': ' + formatPeriod(item.end_period) : ''}>
												{item.method === 'none' ? t('fa_not_depreciated') : (item.months_left ?? '')}
											</td>
											<td className={'text-center text-nowrap ' + statusClass(item.status)}
												title={item.disposed_at ? formatDate(item.disposed_at) : ''}>
												{statusLabel(t, item.status)}
											</td>
										</tr>
									))}
									{data.length > 0 &&
										<tr className="fw-bold">
											<td colSpan="6">{t('total')}</td>
											<td className="text-end text-nowrap">{formatMoney(totals.cost)}</td>
											<td className="text-end text-nowrap">{formatMoney(totals.accumulated)}</td>
											<td className="text-end text-nowrap">{formatMoney(totals.residual)}</td>
											<td className="text-end text-nowrap">{formatMoney(totals.monthly)}</td>
											<td colSpan="2"></td>
										</tr>
									}
									{data.length === 0 &&
										<tr>
											<td colSpan="12" className="text-center text-muted py-4">
												{t('fa_empty')}
											</td>
										</tr>
									}
								</tbody>
							</table>
						</div>
					</div>
				</div>
			}

			{modal.show &&
				<AssetModal
					id={modal.id}
					poses={poses}
					references={references}
					defaultPosId={reduxAccount.pos_id}
					onClose={closeCard}
					onPrintLabel={(item) => printLabels([item])} />
			}

			<div style={{ 'display': 'none' }}>
				<Labels ref={printRef} items={labels} />
			</div>
		</>
	)
}

export default Index
